"use client";

import CheckIcon from "@mui/icons-material/Check";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import TranslateOutlinedIcon from "@mui/icons-material/TranslateOutlined";
import {
  Button,
  CircularProgress,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from "@mui/material";
import { colorPrimitives, text } from "@rmh/ds";
import { LOCALES, type Locale } from "@rmh/defs";
import { usePathname, useRouter } from "@rmh/i18n";
import { useLocale, useTranslations } from "next-intl";
import { useId, useState, useTransition } from "react";

export const LanguageSwitcher = () => {
  const t = useTranslations("onboardingV2.languageSwitcher");
  const locale = useLocale() as Locale;
  const pathname = usePathname();
  const router = useRouter();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isPending, startTransition] = useTransition();
  const menuId = useId();
  const open = Boolean(anchorEl);

  const select = (next: Locale) => {
    setAnchorEl(null);
    if (next === locale) return;
    startTransition(() => {
      router.replace(`${pathname}${window.location.search}`, { locale: next });
    });
  };

  return (
    <>
      <Button
        aria-label={t("ariaLabel", { language: t(`languages.${locale}`) })}
        aria-haspopup="menu"
        aria-controls={open ? menuId : undefined}
        aria-expanded={open}
        aria-busy={isPending}
        disabled={isPending}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        startIcon={<TranslateOutlinedIcon />}
        endIcon={
          isPending ? (
            <CircularProgress size="1rem" color="inherit" />
          ) : (
            <ExpandMoreIcon
              sx={{
                transition: "transform 150ms ease",
                transform: open ? "rotate(180deg)" : "none",
              }}
            />
          )
        }
        sx={{
          color: text.staticWhite.light,
          paddingX: 1.5,
          minHeight: "2.75rem",
          "&:hover": { backgroundColor: colorPrimitives.blue[800] },
          "&.Mui-disabled": { color: text.staticWhite.light, opacity: 0.7 },
          "&.Mui-focusVisible": {
            outline: `0.125rem solid ${text.staticWhite.light}`,
            outlineOffset: "0.125rem",
          },
        }}
      >
        {t(`languages.${locale}`)}
      </Button>
      <Menu
        id={menuId}
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { marginTop: 1, minWidth: "10rem" } } }}
      >
        {LOCALES.map((option) => (
          <MenuItem
            key={option}
            lang={option}
            role="menuitemradio"
            aria-checked={option === locale}
            selected={option === locale}
            onClick={() => select(option)}
          >
            <ListItemText>{t(`languages.${option}`)}</ListItemText>
            <ListItemIcon sx={{ justifyContent: "flex-end" }}>
              {option === locale && <CheckIcon fontSize="small" />}
            </ListItemIcon>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
